import React from 'react';
import { Search, AlertTriangle, XCircle, Info } from 'lucide-react';

interface DataGap {
  type: 'missing_month' | 'low_activity' | 'spike' | 'drop' | 'missing_category' | 'single_year';
  severity: 'info' | 'warning' | 'error';
  year?: number;
  month?: number;
  category?: string;
  message: string;
  suggestion: string;
  value?: number;
  expectedValue?: number;
}

interface DataGapDetectorProps {
  financialData: Record<string, any[]>;
  reconstructedPL?: {
    by_year: Record<string, { revenue: number; cogs: number; payroll: number; expenses: number }>;
    totals: any;
  };
  eventStartDate?: string;
  eventEndDate?: string;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function detectDataGaps(props: DataGapDetectorProps): DataGap[] {
  const { financialData, reconstructedPL, eventStartDate, eventEndDate } = props;
  const gaps: DataGap[] = [];
  
  if (!financialData || Object.keys(financialData).length === 0) {
    gaps.push({
      type: 'missing_category',
      severity: 'error',
      message: 'No financial data available',
      suggestion: 'Upload bank statements or P&L documents to begin analysis'
    });
    return gaps;
  }

  const years = Object.keys(financialData).filter(k => k.startsWith('data_')).sort();
  
  // Check for single year (need baseline for comparison)
  if (years.length === 1) {
    gaps.push({
      type: 'single_year',
      severity: 'warning',
      message: 'Only 1 year of data available',
      suggestion: 'For accurate BI calculations, upload 2-3 years of historical data for baseline comparison'
    });
  }

  // Analyze each year
  years.forEach(yearKey => {
    const year = parseInt(yearKey.replace('data_', ''));
    const monthlyData = financialData[yearKey] || [];
    
    // Check for missing months
    const monthsWithData = monthlyData.filter((m: any) => 
      (m.revenue && m.revenue > 0) || 
      (m.expenses && Object.values(m.expenses || {}).some((v: any) => v > 0))
    );
    
    if (monthsWithData.length < 12) {
      const missingMonths = [];
      for (let i = 1; i <= 12; i++) {
        const monthData = monthlyData.find((m: any) => m.month === i);
        if (!monthData || ((!monthData.revenue || monthData.revenue === 0) && 
            (!monthData.expenses || Object.keys(monthData.expenses || {}).length === 0))) {
          missingMonths.push(i);
        }
      }
      
      if (missingMonths.length > 0 && missingMonths.length < 12) {
        gaps.push({
          type: 'missing_month',
          severity: missingMonths.length > 3 ? 'warning' : 'info',
          year,
          message: `${year}: Missing data for ${missingMonths.map(m => MONTH_NAMES[m-1]).join(', ')}`,
          suggestion: `Upload bank statements for ${missingMonths.map(m => MONTH_NAMES[m-1]).join(', ')} ${year}`
        });
      }
    }

    // Check for revenue spikes/drops (potential event detection)
    const revenues = monthlyData
      .filter((m: any) => m.revenue && m.revenue > 0)
      .map((m: any) => ({ month: m.month, revenue: m.revenue }));
    
    if (revenues.length >= 3) {
      const avgRevenue = revenues.reduce((sum: number, m: any) => sum + m.revenue, 0) / revenues.length;
      
      revenues.forEach((m: any, idx: number) => {
        // Check for significant drop (>50%)
        if (m.revenue < avgRevenue * 0.5 && avgRevenue > 0) {
          gaps.push({
            type: 'drop',
            severity: 'warning',
            year,
            month: m.month,
            value: m.revenue,
            expectedValue: avgRevenue,
            message: `${MONTH_NAMES[m.month-1]} ${year}: Revenue dropped ${Math.round((1 - m.revenue/avgRevenue) * 100)}% below average`,
            suggestion: 'Is this the disruption event period? Verify event dates match this drop.'
          });
        }
        
        // Check for spike (>200%)
        if (m.revenue > avgRevenue * 2 && avgRevenue > 0) {
          gaps.push({
            type: 'spike',
            severity: 'info',
            year,
            month: m.month,
            value: m.revenue,
            expectedValue: avgRevenue,
            message: `${MONTH_NAMES[m.month-1]} ${year}: Unusual revenue spike (${Math.round(m.revenue/avgRevenue * 100)}% of average)`,
            suggestion: 'Verify this isn\'t a one-time event or data entry error'
          });
        }
      });
    }

    // Check for low activity months
    const lowActivityMonths = monthlyData.filter((m: any) => {
      const totalActivity = (m.revenue || 0) + 
        Object.values(m.expenses || {}).reduce((sum: number, v: any) => sum + (v || 0), 0);
      return totalActivity > 0 && totalActivity < 1000;
    });
    
    if (lowActivityMonths.length > 0) {
      gaps.push({
        type: 'low_activity',
        severity: 'info',
        year,
        message: `${year}: ${lowActivityMonths.length} month(s) with minimal activity (<$1,000)`,
        suggestion: 'This may indicate incomplete data or seasonal business patterns'
      });
    }
  });

  // Check for missing expense categories in reconstructed P&L
  if (reconstructedPL?.by_year) {
    const yearTotals = Object.values(reconstructedPL.by_year);
    const hasPayroll = yearTotals.some(y => y.payroll > 0);
    const hasCOGS = yearTotals.some(y => y.cogs > 0);
    const hasExpenses = yearTotals.some(y => y.expenses > 0);
    
    if (!hasPayroll) {
      gaps.push({
        type: 'missing_category',
        severity: 'warning',
        category: 'payroll',
        message: 'No payroll transactions detected',
        suggestion: 'Upload payroll records or W-2s for accurate labor cost analysis'
      });
    }
    
    if (!hasExpenses) {
      gaps.push({
        type: 'missing_category',
        severity: 'warning',
        category: 'expenses',
        message: 'Limited expense data detected',
        suggestion: 'Upload credit card statements to capture more expense categories'
      });
    }
  }

  // Check if event dates fall within data range
  if (eventStartDate && years.length > 0) {
    const eventYear = parseInt(eventStartDate.split('-')[0]);
    const dataYears = years.map(y => parseInt(y.replace('data_', '')));
    
    if (!dataYears.includes(eventYear)) {
      gaps.push({
        type: 'missing_month',
        severity: 'error',
        year: eventYear,
        message: `Event year (${eventYear}) not in uploaded data`,
        suggestion: `Upload financial records for ${eventYear} to analyze the disruption period`
      });
    }
  }

  // Sort by severity
  const severityOrder = { error: 0, warning: 1, info: 2 };
  gaps.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

  return gaps;
}

export default function DataGapDetector(props: DataGapDetectorProps) {
  const gaps = detectDataGaps(props);
  
  if (gaps.length === 0) {
    return (
      <div className="border rounded-lg p-4" style={{ backgroundColor: 'transparent', borderColor: 'var(--color-verified)' }}>
        <div className="flex items-center gap-2" style={{ color: 'var(--color-verified)' }}>
          <div>
            <p className="font-medium">Data Quality: Good</p>
            <p className="text-sm" style={{ color: 'var(--color-verified)' }}>No significant gaps detected in your financial data</p>
          </div>
        </div>
      </div>
    );
  }

  const errorCount = gaps.filter(g => g.severity === 'error').length;
  const warningCount = gaps.filter(g => g.severity === 'warning').length;
  const infoCount = gaps.filter(g => g.severity === 'info').length;

  return (
    <div className="border rounded-lg overflow-hidden" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      {/* Header */}
      <div className="border-b px-4 py-3" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Search size={20} style={{ color: 'var(--color-needs-human-text)' }} />
            <h3 className="font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>Data Quality Analysis</h3>
          </div>
          <div className="flex items-center gap-3 text-xs">
            {errorCount > 0 && (
              <span className="px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                {errorCount} critical
              </span>
            )}
            {warningCount > 0 && (
              <span className="px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' }}>
                {warningCount} warnings
              </span>
            )}
            {infoCount > 0 && (
              <span className="px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                {infoCount} suggestions
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Gaps List */}
      <div className="divide-y divide-[color:var(--color-border-faint)] max-h-64 overflow-y-auto">
        {gaps.map((gap, idx) => (
          <div
            key={idx}
            className="px-4 py-3"
            style={
              gap.severity === 'error' ? { backgroundColor: 'var(--color-needs-human-tint)' } :
              gap.severity === 'warning' ? { backgroundColor: 'var(--color-needs-human-tint)' } : { backgroundColor: 'var(--color-needs-human-tint)' }
            }
          >
            <div className="flex items-start gap-3">
              <span className="mt-0.5">
                {gap.severity === 'error' ? <XCircle size={16} /> :
                 gap.severity === 'warning' ? <AlertTriangle size={16} /> : <Info size={16} />}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm" style={{
                  color: gap.severity === 'error' ? 'var(--color-needs-human-text)' :
                  gap.severity === 'warning' ? 'var(--color-brand)' : 'var(--color-needs-human-text)'
                }}>
                  {gap.message}
                </p>
                <p className="text-xs mt-1" style={{
                  color: gap.severity === 'error' ? 'var(--color-needs-human-text)' :
                  gap.severity === 'warning' ? 'var(--color-brand)' : 'var(--color-needs-human-text)'
                }}>
                  {gap.suggestion}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="border-t px-4 py-2" style={{ backgroundColor: 'var(--color-bg-base)', borderColor: 'var(--color-border)' }}>
        <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
          Addressing these gaps will improve calculation accuracy and court defensibility
        </p>
      </div>
    </div>
  );
}
